/**
 * StartFromTemplateCard — empty-state card surfaced inside the
 * StructuredCarePlanEditor when the plan has zero structured rows.
 *
 * Calls the existing `POST /api/careplans/from-structured-template/{code}`
 * endpoint (P3). Server-side RBAC + flag gate apply. Confirmation
 * dialog precedes the network call.
 *
 * NOTE: This card creates a NEW DRAFT plan (per the existing template
 * endpoint contract). It does NOT overwrite the current plan. After
 * apply, the caller should navigate to the newly created plan. We
 * surface the new plan_id via `onCreated`.
 */
import React, { useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TEMPLATES = [
  { code: 'generic_ndis', name: 'Generic NDIS Support Plan',
    desc: 'Default starter — communication, mobility, daily living, goals.' },
  { code: 'sil_house', name: 'SIL House Support Plan',
    desc: 'Shared house — standing orders, sleepover protocols, meals + meds.' },
  { code: 'centre_based', name: 'Centre-Based Support Plan',
    desc: 'Day centre — groups, transport handoffs, evacuation.' },
];

export function StartFromTemplateCard({ participantId, canCreate, onCreated }) {
  const [confirming, setConfirming] = useState(null);   // template code
  const [busy, setBusy] = useState(false);

  if (!canCreate) {
    return (
      <Card data-testid="structured-empty-state-noperm">
        <CardContent className="p-6 text-sm text-muted-foreground italic text-center">
          This plan has no structured rows yet. Ask a coordinator to start
          from a template or add the first row.
        </CardContent>
      </Card>
    );
  }

  const instantiate = async (code) => {
    setBusy(true);
    try {
      const r = await api.post(`/careplans/from-structured-template/${code}`, {
        participant_id: participantId,
        title: TEMPLATES.find(t => t.code === code)?.name,
      });
      toast.success(`Created new DRAFT plan from ${code} (${r.data?.data?.rows ?? 0} rows)`);
      onCreated?.(r.data?.data?.plan_id);
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || 'Template instantiation failed');
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  return (
    <Card data-testid="structured-empty-state-template-card">
      <CardContent className="p-5 space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-violet-700" />
          <h3 className="font-semibold text-sm">Start from a template</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          This plan has no structured rows yet. Pick a starter and we'll create
          a new <strong>DRAFT</strong> plan for this participant pre-populated
          with sensible defaults. Your existing legacy fields are never
          touched.
        </p>
        <div className="grid sm:grid-cols-3 gap-2">
          {TEMPLATES.map((t) => (
            <div key={t.code}
                 className="border rounded p-3 flex flex-col gap-2"
                 data-testid={`structured-template-card-${t.code}`}>
              <div className="font-medium text-sm">{t.name}</div>
              <div className="text-xs text-muted-foreground flex-1">{t.desc}</div>
              <Button size="sm" variant="outline"
                      onClick={() => setConfirming(t.code)}
                      disabled={busy}
                      data-testid={`structured-template-use-${t.code}`}>
                Use this template
              </Button>
            </div>
          ))}
        </div>
        {confirming && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
               data-testid="structured-template-confirm">
            <Card className="w-[460px] max-w-full">
              <CardContent className="p-4 space-y-3">
                <h4 className="font-semibold text-sm">Create a new DRAFT plan?</h4>
                <p className="text-xs text-muted-foreground">
                  This creates a brand-new DRAFT structured plan
                  pre-populated from <strong>{confirming}</strong>. It will
                  not modify the current plan, and any existing structured
                  rows on other plans are untouched.
                </p>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost"
                          onClick={() => setConfirming(null)} disabled={busy}>
                    Cancel
                  </Button>
                  <Button size="sm"
                          onClick={() => instantiate(confirming)}
                          disabled={busy}
                          data-testid="structured-template-confirm-apply">
                    {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                    Create DRAFT
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default StartFromTemplateCard;
